import { ReactNode, useEffect, useState } from 'react'
import ChatRoom from '../Student/ChatRoom'
import { AppDispatch, RootState } from '@/redux/store'
import { useDispatch, useSelector } from 'react-redux'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { useParams } from 'react-router-dom'
import OGESidebar from '../OGE/Sidebar/OGESidebar'
import OGESidebarContent from '../OGE/Sidebar/OGESidebarContent'
import OGESidebarHeader from '../OGE/Sidebar/OGESidebarHeader'
import { SidebarProvider } from '../ui/sidebar'
import StudentSidebarSkeleton from '../Student/skeletons/StudentSidebarSkeleton'
import {
  connectDecisionWebSocket,
  disconnectDecisionWebSocket,
} from '@/redux/asyncThunks/studentDecisionWebsocket'

import { getAccessToken } from '@/utils/authUtils'
import { setDecisions } from '@/redux/slices/studentDecisionWebsocketSlice'
import { UserRole } from '@/constants/room'
import RoomNotFound from '../PageNotFound/RoomNotFound'
import { fetchTeamsByRoomId } from '@/redux/asyncThunks/team'
import {
  applyDefaultOpenRounds,
  buildScheduledReleaseRoundByEventId,
  groupEventsByRound,
} from '@/utils/groupEventsByRound'
import { useGamePhase } from '@/hooks/useGamePhase'

const AdjudicatingDecision = () => {
  const [openEvents, setOpenEvents] = useState<{ [key: number]: boolean }>({})
  const [openRounds, setOpenRounds] = useState<Record<number, boolean>>({})
  const { currentRound } = useGamePhase()
  const timerConfig = useSelector(
    (state: RootState) => state.timerConfiguration.config
  )
  const { currentRoom, isLoading, error } = useSelector(
    (state: RootState) => state.room
  )
  const user = useSelector((state: RootState) => state.user.user)
  const params = useParams()
  const roomId = params.roomId as string
  const dispatch = useDispatch<AppDispatch>()
  const jwtToken = getAccessToken()

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
      dispatch(getRoomMembers(roomId))
      dispatch(fetchTeamsByRoomId(roomId))
    }
  }, [roomId])


  useEffect(() => {
    if (!currentRoom?.events?.length) return
    const scheduledReleaseByEventId = buildScheduledReleaseRoundByEventId(
      timerConfig?.roundConfigurations
    )
    const grouped = groupEventsByRound(currentRoom.events, {
      includeUnreleased: true,
      scheduledReleaseByEventId,
    })
    setOpenRounds((prev) => applyDefaultOpenRounds(prev, grouped, currentRound))
  }, [currentRoom?.events, currentRound, timerConfig?.roundConfigurations])

  useEffect(() => {
    if (jwtToken && roomId) {
      dispatch(setDecisions([]))
      dispatch(
        connectDecisionWebSocket({
          jwtToken,
          roomId,
        })
      )
    }

    return () => {
      dispatch(disconnectDecisionWebSocket())
    }
  }, [dispatch, jwtToken, roomId])

  if (isLoading) {
    return <StudentSidebarSkeleton />
  }

  if (error) {
    return (
      <div className='w-full h-full flex justify-center items-center text-center'>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  const customFormatMessage = (
    content: string,
    selectedStudent?: string
  ): ReactNode => {
    if (!selectedStudent) return content

    return (
      <>
        <span className='text-dark-blue'>
          [Adjudication for {selectedStudent}]{' '}
        </span>
        {content}
      </>
    )
  }

  return (
    <SidebarProvider className='h-screen'>
      <OGESidebar
        header={
          <OGESidebarHeader
            userName={user?.name || 'Professor Name'}
            avatarSrc={user?.profile_picture || ''}
          />
        }
        content={
          <OGESidebarContent
            openEvents={openEvents}
            onEventToggle={(eventId, isOpen) =>
              setOpenEvents((prev) => ({
                ...prev,
                [eventId]: isOpen,
              }))
            }
            openRounds={openRounds}
            onRoundToggle={(roundKey, isOpen) =>
              setOpenRounds((prev) => ({
                ...prev,
                [roundKey]: isOpen,
              }))
            }
          />
        }
      />
      <div className='flex flex-col flex-1 min-w-0 min-h-0 overflow-x-hidden'>
        <ChatRoom
          showAdvisors={false}
          formatMessage={customFormatMessage}
          showNavbar={true}
          showTabbar={false}
          isWhiteCell={user?.role === UserRole.Professor}
        />
      </div>
    </SidebarProvider>
  )
}

export default AdjudicatingDecision
